export type TDiffPart = { text: string, changed: boolean };

// words keep their punctuation for display but compare without it, case-insensitively, with ’ as '
const normalize = (word: string) => word.toLowerCase().replace(/[’‘]/g, '\'').replace(/[^\p{L}\p{N}']+/gu, '');

// The words between the prefix and the suffix common to ALL choices are "changed" — the same zone in every
// choice, so none stands out and gives the answer away. One part per zone, so the underline has no gaps.
export const splitChoiceDiff = (choices: string[]): TDiffPart[][] => {
	const tokens = choices.map(c => c.split(/(\s+)/)); // words at even indices, whitespace at odd
	const words = tokens.map(t => t.filter((_, i) => i % 2 === 0).map(normalize));
	const shortest = Math.min(...words.map(w => w.length));
	const sameAt = (pick: (w: string[]) => string) => words.every(w => pick(w) === pick(words[0]));

	let start = 0;
	while (start < shortest && sameAt(w => w[start])) start++;
	let end = 0;
	while (end < shortest - start && sameAt(w => w[w.length - 1 - end])) end++;

	// no marks for the question when the zone is every whole choice (says nothing), or when it's empty in some
	// choice (a prefix of another, "I go" / "I go home") — that one bare choice would stand out
	const zoneIsWhole = start === 0 && end === 0;
	const zoneEmptyInSome = words.some(w => w.length - 1 - end < start);
	return tokens.map((parts, ci) => {
		const last = words[ci].length - 1 - end;
		if (choices.length < 2 || zoneIsWhole || zoneEmptyInSome) return [{ text: choices[ci], changed: false }];
		return [
			{ text: parts.slice(0, start * 2).join(''), changed: false },
			{ text: parts.slice(start * 2, last * 2 + 1).join(''), changed: true },
			{ text: parts.slice(last * 2 + 1).join(''), changed: false },
		].filter(part => part.text);
	});
};
